import { BadgeCheck, Briefcase, ClipboardCheck, Gift, Laptop, Mail, Trophy, Wallet } from "lucide-react";
import type { NavItem } from "../../components/Header/useNavLinks";

// Conteúdo da landing da Calriz University (public/universidadeCalriz.docx). Programa só no Brasil, por isso
// o texto fica aqui em português e não nos arquivos de tradução.

// header desta página: as seções da própria landing (âncoras) e o botão de inscrição no lugar do "Fale Conosco"
export const HEADER_LINKS: NavItem[] = [
  { label: "Como funciona", to: "#como-funciona" },
  { label: "Regras e benefícios", to: "#regras" },
  { label: "Linha do tempo", to: "#linha-do-tempo" },
  { label: "Perguntas frequentes", to: "#duvidas" },
];

export const HEADER_CTA = { label: "Inscrever-se", to: "#inscricao" };

// faixa depois do formulário: volta para o site institucional
export const BACK_TO_SITE = "Voltar para o site";

export const HIGHLIGHTS = [
  { key: "free", Icon: Gift, title: "100% gratuito", text: "Cursos e laboratórios na plataforma" },
  { key: "vouchers", Icon: Trophy, title: "Vouchers oficiais", text: "Para os melhores colocados no simulado" },
  { key: "bonus", Icon: Wallet, title: "R$ 1.000 de premiação", text: "Para os alunos que obtiverem sucesso na certificação*" },
  { key: "internship", Icon: Briefcase, title: "Programa de estágio", text: "Entrada direta no funil de seleção" },
];

// short: rótulo curto usado no passe do aluno, no hero
export const STEPS = [
  {
    Icon: Mail,
    short: "Inscrição",
    title: "Inscrição e confirmação",
    text: "Preencha seu cadastro nesta página e aguarde a confirmação de acesso por e-mail, com as orientações para criar sua conta na plataforma.",
  },
  {
    Icon: Laptop,
    short: "Treinamento",
    title: "Treinamento na plataforma",
    text: "Acesse a plataforma de ensino, estude no seu ritmo e conclua as trilhas de treinamento e os laboratórios práticos.",
  },
  {
    Icon: ClipboardCheck,
    short: "Simulado",
    title: "Simulado e disputa do voucher",
    text: "Faça o simulado oficial na plataforma. As melhores pontuações garantem o voucher 100% gratuito para o exame de certificação.",
  },
  {
    Icon: BadgeCheck,
    short: "Certificação",
    title: "Certificação e Conclusão do programa",
    text: "Faça o exame oficial com o voucher recebido e siga as instruções para a receber premiação de R$ 1.000,00.",
  },
  {
    Icon: Briefcase,
    short: "Carreira",
    title: "Programa de estágio e carreira",
    text: "Com a certificação concluída, você entra automaticamente no funil de seleção do programa de estágio e das vagas da Calriz.",
  },
];

export const RULES = [
  {
    Icon: Mail,
    title: "Inscrição e seleção por e-mail",
    text: "Inscreva-se gratuitamente nesta página. As orientações para criar sua conta na plataforma de cursos chegam direto no seu e-mail.",
    badge: { label: "Acesso", value: "Via e-mail" },
  },
  {
    Icon: Trophy,
    title: "Treinamento e simulado competitivo",
    text: "Conclua os cursos na plataforma e faça o simulado. Os candidatos com as melhores pontuações ganham o voucher oficial.",
    badge: { label: "Seleção", value: "Maior pontuação" },
  },
  {
    Icon: Wallet,
    title: "Premiação de R$ 1.000,00",
    text: "Faça o exame oficial e siga as instruções para receber o incentivo financeiro de R$ 1.000,00 via PIX ou depósito.",
    badge: { label: "Incentivo", value: "R$ 1.000,00" },
  },
  {
    Icon: Briefcase,
    title: "Funil de estágio e oportunidades",
    text: "Certificados e aprovados avançam para o Programa de Estágio (R$ 1.500/mês por 6 meses) ou recebem propostas de contratação de acordo com o perfil.",
    badge: { label: "Carreira", value: "Estágio e vagas" },
  },
];

// linha do tempo do programa: período e regra de cada etapa (nem toda etapa tem os dois)
export const TIMELINE = [
  {
    Icon: Laptop,
    title: "Treinamento na plataforma",
    period: "01/12/2026 a 31/01/2027",
  },
  {
    Icon: ClipboardCheck,
    title: "Simulado e disputa do voucher",
    period: "Final de janeiro de 2027 (final do período de treinamento)",
    rule: "Os primeiros 50 participantes que fizerem o simulado individual e atingirem 90% de pontuação recebem o voucher para a prova de certificação.",
  },
  {
    Icon: BadgeCheck,
    title: "Certificação e aprovação",
    period: "A partir de fevereiro de 2027 (com prazo de manutenção de 60 dias, indo até abril)",
    rule: "O participante deve passar na prova e finalizar as etapas seguintes em fevereiro de 2027",
  },
  {
    Icon: Briefcase,
    title: "Programa de estágio e carreira",
    rule: "Os finalistas poderão ser convidados para um estágio com duração mínima de 6 meses e remuneração de pelo menos R$ 1.500,00/mês. Dependendo dos seus conhecimentos e experiência, o candidato poderá entrar efetivamente nos quadros da empresa.",
  },
];

export const FAQ = [
  {
    q: "Como recebo o acesso à plataforma de cursos?",
    a: "Depois de fazer sua inscrição nesta página, você recebe um e-mail com as instruções detalhadas para criar sua conta na plataforma e começar os treinamentos.",
  },
  {
    q: "Como funciona a distribuição dos vouchers de certificação?",
    a: "Todos os inscritos podem fazer os treinamentos e o simulado dentro da plataforma. Os vouchers oficiais de certificação são concedidos aos candidatos que alcançarem as melhores pontuações no simulado.",
  },
  {
    q: "O que preciso fazer para receber o incentivo financeiro de R$ 1.000?",
    a: "Depois de fazer a prova de certificação com o seu voucher e ser aprovado, você deve seguir os procedimentos internos da Calriz. Com todas as etapas confirmadas, a premiação de R$ 1.000,00 é paga via PIX ou depósito bancário.",
  },
  {
    q: "Como funciona o ingresso no Programa de Estágio?",
    a: "Quem conquista a certificação oficial e conclui todas as etapas, entra direto no funil de seleção do Programa de Estágio (bolsa de R$ 1.500,00/mês por 6 meses) ou recebe propostas personalizadas de acordo com o perfil técnico e a senioridade.",
  },
];

// o que acontece depois da inscrição (bloco escuro do formulário)
export const NEXT_STEPS = [
  "Inscrição gratuita nesta página",
  "Acesso à plataforma liberado por e-mail",
  "Treinamento, simulado e disputa do voucher",
];

export const EDUCATION_OPTIONS = [
  "Ensino médio completo",
  "Curso técnico",
  "Graduação em andamento",
  "Graduação concluída",
  "Pós-graduação",
];
